import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faClock,
  faCalendarAlt,
  faPlay,
  faPause,
  faRocket,
  faTrash,
  faHistory,
  faCheckCircle,
  faExclamationCircle,
  faDollarSign,
  faChevronDown,
  faChevronRight,
  faCalculator,
  faEdit,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import StatusBadge from './StatusBadge';
import BulkAutoPacingEditModal from './BulkAutoPacingEditModal';
import './BulkAutoPacingDetails.css';

const POLLING_INTERVAL = 5000; // 5 seconds

/**
 * BulkAutoPacingDetails - Component for viewing and managing bulk auto-pacing schedule details
 */
const BulkAutoPacingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();

  const [scheduleData, setScheduleData] = useState(null);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [showHistory, setShowHistory] = useState(true); // Expanded by default
  const pollingTimeoutRef = useRef(null);
  const [expandedReasoningRows, setExpandedReasoningRows] = useState(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const toggleReasoningRow = (logId) => {
    setExpandedReasoningRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const fetchScheduleDetails = useCallback(async () => {
    if (!accountId || !id) return;

    try {
      const response = await api.get(getApiUrl(`/api/v2/bulk-auto-pacing/${id}`), {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.status === 200) {
        setScheduleData(response.data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching schedule details:', err);
      setError(err.response?.data?.error || 'Failed to load schedule details');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, id, user]);

  const fetchExecutionHistory = useCallback(async () => {
    if (!accountId || !id) return;

    try {
      const response = await api.get(getApiUrl(`/api/v2/bulk-auto-pacing/${id}/history`), {
        params: { accountId, userId: user?.userId || user?.id, limit: 50 }
      });

      if (response.status === 200) {
        setExecutionHistory(response.data.history || []);
      }
    } catch (err) {
      console.error('Error fetching execution history:', err);
    }
  }, [accountId, id, user]);

  // Initial fetch
  useEffect(() => {
    fetchScheduleDetails();
    fetchExecutionHistory();
  }, [fetchScheduleDetails, fetchExecutionHistory]);

  // Polling effect - always poll every 5 seconds while on this page
  useEffect(() => {
    if (!isLoading) {
      pollingTimeoutRef.current = setTimeout(() => {
        fetchScheduleDetails();
        fetchExecutionHistory();
      }, POLLING_INTERVAL);
    }

    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [isLoading, fetchScheduleDetails, fetchExecutionHistory, scheduleData]);

  const handleToggleStatus = async () => {
    const currentStatus = scheduleData?.schedule?.status;
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      setActionInProgress('toggle');
      await api.patch(
        getApiUrl(`/api/v2/bulk-auto-pacing/${id}`),
        { status: newStatus },
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      fetchScheduleDetails();
    } catch (err) {
      console.error('Error toggling status:', err);
      alert('Failed to update schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRunNow = async () => {
    if (!window.confirm('Are you sure you want to run the auto pacing now? This will refresh campaign data and update budgets.')) {
      return;
    }
    try {
      setActionInProgress('run');
      // Reset execution progress to show fresh state
      setScheduleData(prev => prev ? {
        ...prev,
        executionProgress: {
          total: prev.executionProgress?.total || 0,
          pending: prev.executionProgress?.total || 0,
          processing: 0,
          refreshing: 0,
          calculating: 0,
          editing: 0,
          completed: 0,
          failed: 0,
          percentComplete: 0,
          status: 'running',
          startedAt: new Date().toISOString(),
          completedAt: null
        }
      } : prev);
      await api.post(
        getApiUrl(`/api/v2/bulk-auto-pacing/${id}/run`),
        {},
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      // Refresh immediately
      fetchScheduleDetails();
      fetchExecutionHistory();
    } catch (err) {
      console.error('Error triggering execution:', err);
      alert('Failed to trigger execution: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this auto pacing schedule?')) return;
    try {
      setActionInProgress('delete');
      await api.delete(
        getApiUrl(`/api/v2/bulk-auto-pacing/${id}`),
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      navigate('/pacing');
    } catch (err) {
      console.error('Error deleting schedule:', err);
      alert('Failed to delete schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleEditSchedule = async (scheduleData) => {
    try {
      setIsEditSubmitting(true);
      await api.patch(
        getApiUrl(`/api/v2/bulk-auto-pacing/${id}`),
        scheduleData,
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      setShowEditModal(false);
      fetchScheduleDetails(); // Refresh the data
    } catch (err) {
      console.error('Error updating schedule:', err);
      alert('Failed to update schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const getPlatformName = (platform) => {
    const names = {
      'dv360': 'DV360',
      'ttd': 'The Trade Desk',
      'facebook': 'Facebook',
      'meta': 'Meta',
      'google_search': 'Google Ads',
      'google-ads-search': 'Google Ads Search',
      'google_ads': 'Google Ads Search',
      'google_ads_search': 'Google Ads Search',
      'tiktok': 'TikTok',
      'reddit': 'Reddit',
      'linkedin': 'LinkedIn',
      'madhive': 'MadHive'
    };
    // Try direct match first, then lowercase match
    return names[platform] || names[platform?.toLowerCase()] || 'Unknown';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Parse as UTC to avoid timezone shift (DB DATE columns are timezone-unaware).
    // "2026-02-01T00:00:00.000Z" in local time (e.g. US/Eastern) would show as Jan 31.
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      timeZone: 'UTC'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2
    }).format(value || 0);
  };

  /**
   * Download the original uploaded Excel file
   */
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);

  const downloadOriginalFile = async () => {
    if (!schedule?.hasDownloadableFile || isDownloadingFile) return;

    setIsDownloadingFile(true);
    try {
      const response = await api.get(getApiUrl(`/api/v2/bulk-auto-pacing/${id}/download-file`), {
        params: {
          accountId,
          userId: user?.userId || user?.id
        }
      });

      if (response.data?.publicUrl) {
        // Open the signed URL in a new tab to trigger download
        window.open(response.data.publicUrl, '_blank');
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    } finally {
      setIsDownloadingFile(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bulk-auto-pacing-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading schedule details...</p>
      </div>
    );
  }

  if (error && !scheduleData) {
    return (
      <div className="bulk-auto-pacing-error">
        <p>{error}</p>
        <button onClick={() => navigate('/pacing')}>Back to Pacing</button>
      </div>
    );
  }

  if (!scheduleData) return null;

  const { schedule, campaigns, progress, executionProgress } = scheduleData;

  // Calculate new daily budget using the same formula as backend
  // Formula: (Total Media Budget - Budget Spent) / Days Remaining
  const calculateNewDailyBudget = (totalBudget, deliveredToDate, campaignFlightEndDate = null) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Use per-campaign flight end date if available, otherwise use schedule's
    const flightEndDateStr = campaignFlightEndDate || schedule.flight_end_date;
    const endDate = new Date(flightEndDateStr);
    endDate.setHours(0, 0, 0, 0);

    // Calculate days remaining (including today)
    const timeDiff = endDate.getTime() - today.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1;

    // Calculate remaining budget
    const remainingBudget = totalBudget - deliveredToDate;

    // Calculate new daily budget
    let calculatedBudget = daysRemaining > 0 ? remainingBudget / daysRemaining : 0;

    // Round to 2 decimal places
    calculatedBudget = Math.round(calculatedBudget * 100) / 100;

    // Apply minimum budget (use schedule's min or default to $1)
    const minDailyBudget = parseFloat(schedule.min_daily_budget) || 1.00;
    if (calculatedBudget < minDailyBudget && calculatedBudget >= 0) {
      calculatedBudget = minDailyBudget;
    }

    return calculatedBudget;
  };

  return (
    <div className="bulk-auto-pacing-container">
      {/* Header */}
      <div className="bulk-auto-pacing-header">
        <button onClick={() => navigate('/pacing')} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Pacing
        </button>

        <div className="schedule-info">
          <h1>{schedule.name}</h1>
          <div className="schedule-meta">
            <span className="platform">{getPlatformName(schedule.platform)}</span>
            <span className="folder">{schedule.folder_name || 'No folder'}</span>
            <StatusBadge status={schedule.status} />
          </div>
        </div>

        <div className="schedule-actions">
          {actionInProgress ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setShowHistory(!showHistory)}
                title="View History"
              >
                <FontAwesomeIcon icon={faHistory} /> History
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditModal(true)}
                title="Edit Schedule"
              >
                <FontAwesomeIcon icon={faEdit} /> Edit
              </button>
              <button
                className="btn btn-primary"
                onClick={handleRunNow}
                disabled={schedule.status !== 'active'}
                title="Run Now"
              >
                <FontAwesomeIcon icon={faRocket} /> Run Now
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleToggleStatus}
                title={schedule.status === 'active' ? 'Pause' : 'Resume'}
              >
                <FontAwesomeIcon icon={schedule.status === 'active' ? faPause : faPlay} />
                {schedule.status === 'active' ? 'Pause' : 'Resume'}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                title="Delete"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Import Progress Section - always show */}
      {progress && (
        <div className="pacing-progress-section">
          <div className="progress-header">
            <h2>
              {progress.percentComplete === 100 ? 'Import Complete' : 'Import Progress'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {progress.percentComplete < 100 && (
                <span className="polling-indicator">
                  <FontAwesomeIcon icon={faSpinner} spin /> Auto-refreshing every 5s
                </span>
              )}
              {progress.percentComplete === 100 && progress.completedAt && (
                <span style={{ fontSize: '0.85em', color: '#666' }}>
                  {progress.startedAt && (
                    <>
                      Duration: {(() => {
                        const start = new Date(progress.startedAt);
                        const end = new Date(progress.completedAt);
                        const diffMs = end - start;
                        const diffSecs = Math.floor(diffMs / 1000);
                        const mins = Math.floor(diffSecs / 60);
                        const secs = diffSecs % 60;
                        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                      })()}
                      {' • '}
                    </>
                  )}
                  Completed: {new Date(progress.completedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill completed"
                style={{ width: `${(progress.completed / progress.total) * 100}%` }}
              />
              <div
                className="progress-fill failed"
                style={{ width: `${(progress.failed / progress.total) * 100}%` }}
              />
            </div>
            <span className="progress-text">{progress.percentComplete}%</span>
          </div>
          <div className="progress-stats">
            <div className="stat">
              <span className="stat-value">{progress.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat pending">
              <span className="stat-value">{progress.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat processing">
              <span className="stat-value">{progress.processing}</span>
              <span className="stat-label">Importing</span>
            </div>
            <div className="stat completed">
              <span className="stat-value">{progress.completed}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat failed">
              <span className="stat-value">{progress.failed}</span>
              <span className="stat-label">Failed</span>
            </div>
          </div>
        </div>
      )}

      {/* Execution Progress Section - show during execution OR after completion if there are results */}
      {executionProgress && (executionProgress.status === 'running' || executionProgress.completed > 0 || executionProgress.failed > 0) && (
        <div className="pacing-progress-section execution-progress">
          <div className="progress-header">
            <h2>
              {executionProgress.status === 'running' ? 'Execution Progress' : 'Latest Execution Results'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {executionProgress.status === 'running' && (
                <span className="polling-indicator">
                  <FontAwesomeIcon icon={faSpinner} spin /> Auto-refreshing every 5s
                </span>
              )}
              {executionProgress.startedAt && (
                <span style={{ fontSize: '0.85em', color: '#666' }}>
                  {executionProgress.status === 'running' ? (
                    <>Started: {new Date(executionProgress.startedAt).toLocaleString()}</>
                  ) : executionProgress.completedAt ? (
                    <>
                      Duration: {(() => {
                        const start = new Date(executionProgress.startedAt);
                        const end = new Date(executionProgress.completedAt);
                        const diffMs = end - start;
                        const diffSecs = Math.floor(diffMs / 1000);
                        const mins = Math.floor(diffSecs / 60);
                        const secs = diffSecs % 60;
                        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                      })()}
                      {' • '}Completed: {new Date(executionProgress.completedAt).toLocaleString()}
                    </>
                  ) : null}
                </span>
              )}
            </div>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill completed"
                style={{ width: `${(executionProgress.completed / executionProgress.total) * 100}%` }}
              />
              <div
                className="progress-fill failed"
                style={{ width: `${(executionProgress.failed / executionProgress.total) * 100}%` }}
              />
            </div>
            <span className="progress-text">{executionProgress.percentComplete}%</span>
          </div>
          <div className="progress-stats">
            <div className="stat">
              <span className="stat-value">{executionProgress.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat pending">
              <span className="stat-value">{executionProgress.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat processing">
              <span className="stat-value">{executionProgress.refreshing}</span>
              <span className="stat-label">Refreshing</span>
            </div>
            <div className="stat processing">
              <span className="stat-value">{executionProgress.calculating}</span>
              <span className="stat-label">Calculating</span>
            </div>
            <div className="stat processing">
              <span className="stat-value">{executionProgress.editing}</span>
              <span className="stat-label">Editing</span>
            </div>
            {executionProgress.retrying > 0 && (
              <div className="stat retrying">
                <span className="stat-value">{executionProgress.retrying}</span>
                <span className="stat-label">Retrying</span>
              </div>
            )}
            <div className="stat completed">
              <span className="stat-value">{executionProgress.completed}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat failed">
              <span className="stat-value">{executionProgress.failed}</span>
              <span className="stat-label">Failed</span>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Configuration */}
      <div className="schedule-config-section">
        <h2>Schedule Configuration</h2>
        <div className="config-grid">
          <div className="config-item">
            <label><FontAwesomeIcon icon={faCalendarAlt} /> Flight Period</label>
            <span>{formatDate(schedule.flight_start_date)} - {formatDate(schedule.flight_end_date)}</span>
          </div>
          <div className="config-item">
            <label><FontAwesomeIcon icon={faClock} /> Execution Time</label>
            <span>{schedule.execution_time?.substring(0, 5) || '09:00'} ({schedule.timezone || 'UTC'})</span>
          </div>
          <div className="config-item">
            <label><FontAwesomeIcon icon={faDollarSign} /> Min Daily Budget</label>
            <span>{formatCurrency(schedule.min_daily_budget)}</span>
          </div>
          <div className="config-item">
            <label>Next Execution</label>
            <span>{schedule.next_execution_at ? formatDateTime(schedule.next_execution_at) : 'Not scheduled'}</span>
          </div>
        </div>
      </div>

      {/* Campaigns Section */}
      <div className="campaigns-section">
        <h2>Campaigns ({campaigns?.length || 0})</h2>
        {campaigns && campaigns.length > 0 ? (
          <div className="campaigns-table-container">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Campaign ID</th>
                  <th>Campaign Name</th>
                  <th>Status</th>
                  <th>Exec Status</th>
                  <th>Retries</th>
                  <th>Flight Start</th>
                  <th>Flight End</th>
                  <th>Total Budget</th>
                  <th>Delivered to Date</th>
                  <th>Delivered %</th>
                  <th>Remaining Budget</th>
                  <th>New Daily Budget</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => {
                  // Use current_delivered_to_date (updated by scheduler) with fallback to initial (from Excel)
                  const deliveredToDate = campaign.current_delivered_to_date ?? campaign.initial_delivered_to_date ?? 0;
                  const totalBudget = campaign.total_media_budget || 0;
                  const remainingBudget = totalBudget - deliveredToDate;
                  const deliveredPercent = totalBudget > 0 ? (deliveredToDate / totalBudget) * 100 : 0;
                  // Use per-campaign flight dates if available, otherwise use schedule dates
                  const flightStart = campaign.flight_start_date || schedule.flight_start_date;
                  const flightEnd = campaign.flight_end_date || schedule.flight_end_date;
                  // Priority: 1) last_calculated_budget (from scheduler run)
                  //           2) initial_daily_budget (from Excel)
                  //           3) calculate on the fly using campaign's flight end date
                  const newDailyBudget = campaign.last_calculated_budget ??
                    campaign.initial_daily_budget ??
                    calculateNewDailyBudget(totalBudget, deliveredToDate, flightEnd);

                  return (
                    <tr key={campaign.id} className={campaign.status === 'error' ? 'row-error' : ''}>
                      <td className="mono">{campaign.platform_campaign_id}</td>
                      <td>
                        {campaign.campaign_id ? (
                          <Link to={`/campaigns/${campaign.campaign_id}`} className="campaign-link">
                            {campaign.campaign_name || campaign.excel_campaign_name || 'View Campaign'}
                          </Link>
                        ) : (
                          campaign.excel_campaign_name || '-'
                        )}
                      </td>
                      <td>
                        <StatusBadge status={campaign.status} />
                      </td>
                      <td>
                        {campaign.status === 'active' && campaign.execution_status && (
                          <StatusBadge status={campaign.execution_status} />
                        )}
                        {campaign.status !== 'active' && '-'}
                      </td>
                      <td className="retry-cell">
                        {campaign.retry_count > 0 ? (
                          <span className="retry-info" title={campaign.current_operation ? `Current operation: ${campaign.current_operation}` : ''}>
                            {campaign.retry_count}/{campaign.max_retries || 3}
                            {campaign.current_operation && ['retrying_refresh', 'retrying_edit'].includes(campaign.execution_status) && (
                              <span className="retry-operation"> ({campaign.current_operation})</span>
                            )}
                          </span>
                        ) : (
                          <span className="retry-none">-</span>
                        )}
                      </td>
                      <td>{flightStart ? formatDate(flightStart) : '-'}</td>
                      <td>{flightEnd ? formatDate(flightEnd) : '-'}</td>
                      <td>{formatCurrency(totalBudget)}</td>
                      <td>{formatCurrency(deliveredToDate)}</td>
                      <td className={deliveredPercent > 100 ? 'over-budget' : ''}>
                        {deliveredPercent.toFixed(2)}%
                      </td>
                      <td className={remainingBudget < 0 ? 'negative-value' : ''}>
                        {formatCurrency(remainingBudget)}
                      </td>
                      <td className={newDailyBudget < 0 ? 'negative-value' : ''}>
                        {formatCurrency(newDailyBudget)}
                      </td>
                      <td className="error-cell" title={campaign.last_error || campaign.last_execution_error || ''}>
                        {campaign.status === 'error' ? 'Encountered error during import' :
                         campaign.execution_status === 'failed' ? 'Encountered error during execution' : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-campaigns">No campaigns in this schedule</p>
        )}
      </div>

      {/* Execution History Section */}
      {showHistory && (
        <div className="execution-history-section">
          <h2><FontAwesomeIcon icon={faHistory} /> Execution History</h2>
          {executionHistory.length > 0 ? (
            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Date</th>
                    <th>Campaign</th>
                    <th>Previous Budget</th>
                    <th>New Budget</th>
                    <th>Status</th>
                    <th>Retries</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {executionHistory.map((log) => {
                    const isExpanded = expandedReasoningRows.has(log.id);
                    // Show expandable reasoning for ANY status that has reasoning (success, skipped, failed)
                    const hasReasoning = !!log.calculation_reasoning;
                    const totalRetries = (log.refresh_retry_count || 0) + (log.edit_retry_count || 0);

                    // Determine what to show in message cell:
                    // - For errors: show error_message
                    // - For skipped/failed with reasoning: show truncated reasoning with "click to expand" hint
                    // - Fallback: calculation_details or '-'
                    const getMessageContent = () => {
                      if (log.error_message) return log.error_message;
                      if (log.calculation_reasoning && (log.status === 'skipped' || log.status === 'failed')) {
                        // Show first part of reasoning with ellipsis if longer
                        const reasoning = log.calculation_reasoning;
                        if (reasoning.length > 80) {
                          return reasoning.substring(0, 80) + '... (click ▶ to expand)';
                        }
                        return reasoning;
                      }
                      return log.calculation_details || '-';
                    };

                    return (
                      <React.Fragment key={log.id}>
                        <tr className={`${log.status === 'failed' ? 'failed-row' : ''} ${log.status === 'skipped' ? 'skipped-row' : ''}`}>
                          <td className="expand-cell">
                            {hasReasoning && (
                              <button
                                className="expand-btn"
                                onClick={() => toggleReasoningRow(log.id)}
                                title={isExpanded ? 'Hide details' : 'Show details'}
                              >
                                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                              </button>
                            )}
                          </td>
                          <td>{formatDateTime(log.executed_at)}</td>
                          <td className="mono">{log.platform_campaign_id}</td>
                          <td>{formatCurrency(log.previous_budget)}</td>
                          <td>{formatCurrency(log.new_budget)}</td>
                          <td>
                            <span className={`status-tag ${log.status}`}>
                              {log.status === 'success' ? (
                                <FontAwesomeIcon icon={faCheckCircle} />
                              ) : log.status === 'skipped' ? (
                                <FontAwesomeIcon icon={faExclamationCircle} />
                              ) : (
                                <FontAwesomeIcon icon={faExclamationCircle} />
                              )}
                              {' '}{log.status}
                            </span>
                          </td>
                          <td className="retry-cell">
                            {totalRetries > 0 ? (
                              <span
                                className="retry-info"
                                title={`Refresh: ${log.refresh_retry_count || 0}, Edit: ${log.edit_retry_count || 0}`}
                              >
                                {totalRetries}
                              </span>
                            ) : (
                              <span className="retry-none">-</span>
                            )}
                          </td>
                          <td className="message-cell">{getMessageContent()}</td>
                        </tr>
                        {isExpanded && hasReasoning && (
                          <tr className="reasoning-row">
                            <td colSpan="8">
                              <div className="calculation-reasoning">
                                <div className="reasoning-header">
                                  <FontAwesomeIcon icon={faCalculator} /> {log.status === 'skipped' ? 'Skip Reason' : log.status === 'failed' ? 'Failure Details' : 'Calculation Reasoning'}
                                </div>
                                <pre className="reasoning-content">{log.calculation_reasoning}</pre>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-history">No execution history yet</p>
          )}
        </div>
      )}

      {/* File info */}
      {schedule.original_file_name && (
        <div className="pacing-file-info">
          <FontAwesomeIcon icon={isDownloadingFile ? faSpinner : faDownload} spin={isDownloadingFile} />
          {schedule.hasDownloadableFile ? (
            <span
              className="downloadable-file-link"
              onClick={downloadOriginalFile}
              title="Click to download the original file"
            >
              Original file: {schedule.original_file_name}
            </span>
          ) : (
            <span>Original file: {schedule.original_file_name}</span>
          )}
        </div>
      )}

      {/* Edit Schedule Modal */}
      <BulkAutoPacingEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        schedule={schedule}
        onSubmit={handleEditSchedule}
        isSubmitting={isEditSubmitting}
      />
    </div>
  );
};

export default BulkAutoPacingDetails;

